import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import CircuitMap from '../components/CircuitMap';
import Icon from '../components/Icon';

const SEASON = 2026;

const TEAM_COLORS = {
  'Red Bull Racing': '#3671C6', 'Ferrari': '#E8002D', 'Mercedes': '#27F4D2',
  'McLaren': '#FF8000', 'Aston Martin': '#229971', 'Alpine': '#FF87BC',
  'Williams': '#64C4FF', 'Racing Bulls': '#6692FF', 'Audi': '#52E252',
  'Haas': '#B6BABD', 'Haas F1 Team': '#B6BABD', 'Cadillac': '#CC0000',
};
const teamColor = (team) => TEAM_COLORS[team] || '#71717a';

const fmtDt = (date, time) => {
  if (!date) return '—';
  const dt = time ? new Date(`${date}T${time}`) : new Date(date);
  return dt.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const SessionRow = ({ label, session }) => {
  if (!session?.date) return null;
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-zinc-800 last:border-0">
      <span className="text-zinc-400 text-sm">{label}</span>
      <span className="text-white text-sm font-medium">{fmtDt(session.date, session.time)}</span>
    </div>
  );
};

const PODIUM_STYLE = [
  { bg: 'from-yellow-500/20 to-transparent', border: 'border-yellow-500/40', text: 'text-yellow-400', medal: 'P1', label: 'Vainqueur' },
  { bg: 'from-zinc-400/10 to-transparent', border: 'border-zinc-400/30', text: 'text-zinc-300', medal: 'P2', label: '2e place' },
  { bg: 'from-orange-700/15 to-transparent', border: 'border-orange-700/30', text: 'text-orange-400', medal: 'P3', label: '3e place' },
];

const DriverAvatar = ({ driver, size = 10 }) => {
  const [failed, setFailed] = useState(false);
  const color = teamColor(driver?.team);
  if (driver?.headshot && !failed) {
    return (
      <img src={driver.headshot} alt={driver.fullName}
        className={`w-${size} h-${size} rounded-full object-cover object-top bg-zinc-800`}
        onError={() => setFailed(true)} />
    );
  }
  return (
    <div className={`w-${size} h-${size} rounded-full flex items-center justify-center text-xs font-black`}
      style={{ backgroundColor: `${color}22`, color, border: `1px solid ${color}44` }}>
      {driver?.code?.slice(0, 3) || '?'}
    </div>
  );
};

const DriverModal = ({ driver, season, onClose }) => {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const color = teamColor(driver?.team);

  useEffect(() => {
    if (!driver?.number) return;
    setStatsLoading(true);
    api.get(`/races/driver-stats/${driver.number}?season=${season}`)
      .then(res => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, [driver, season]);

  if (!driver) return null;

  const posLabel = (p) => p <= 3 ? `P${p}` : `P${p}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-fade-up"
        onClick={e => e.stopPropagation()}>

        {/* Header with team color gradient */}
        <div className="relative h-36 flex items-end px-5 pb-0"
          style={{ background: `linear-gradient(135deg, ${color}30 0%, #18181b 100%)` }}>
          <div className="absolute top-3 right-3">
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors">
              <Icon name="x" size={14} className="text-zinc-400" />
            </button>
          </div>
          <div className="absolute bottom-0 right-5">
            <DriverAvatar driver={driver} size={24} />
          </div>
          <div className="pb-3 pr-28">
            <div className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color }}>{driver.team}</div>
            <h2 className="text-2xl font-black text-white leading-tight">{driver.fullName}</h2>
          </div>
        </div>

        {/* Info strip */}
        <div className="flex border-t border-zinc-800 divide-x divide-zinc-800">
          <div className="flex-1 px-4 py-3 text-center">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Numéro</p>
            <p className="text-xl font-black text-white mt-0.5">#{driver.number}</p>
          </div>
          <div className="flex-1 px-4 py-3 text-center">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Pays</p>
            <p className="text-sm font-bold text-white mt-0.5">{driver.code || '—'}</p>
          </div>
          {stats && !statsLoading && (
            <div className="flex-1 px-4 py-3 text-center">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Podiums</p>
              <p className="text-xl font-black mt-0.5" style={{ color }}>{stats.podiums}</p>
            </div>
          )}
        </div>

        {/* Season stats */}
        <div className="px-5 pb-5">
          {statsLoading ? (
            <div className="flex justify-center py-4">
              <div className="w-5 h-5 border-2 border-f1red border-t-transparent rounded-full animate-spin" />
            </div>
          ) : stats && stats.racesRun > 0 ? (
            <>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[
                  { label: 'Courses', val: stats.racesRun },
                  { label: 'Victoires', val: stats.wins },
                  { label: 'Meilleur', val: stats.bestResult ? `P${stats.bestResult}` : '—' },
                ].map(s => (
                  <div key={s.label} className="bg-zinc-800 rounded-lg px-3 py-2 text-center">
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</p>
                    <p className="text-lg font-black text-white mt-0.5">{s.val}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Résultats saison {season}</p>
              <div className="flex flex-col gap-1 max-h-36 overflow-y-auto">
                {stats.results.map((r, i) => (
                  <div key={i} className="flex justify-between items-center text-sm py-1 border-b border-zinc-800/60 last:border-0">
                    <span className="text-zinc-400 truncate">{r.raceName}</span>
                    <span className="font-bold ml-2 shrink-0" style={{ color: r.position <= 3 ? color : undefined }}>{posLabel(r.position)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-zinc-600 text-sm text-center py-3">Pas encore de résultats cette saison</p>
          )}
        </div>
      </div>
    </div>
  );
};

const normGP = (v) => v ? String(v).toUpperCase().trim() : '';
const GP_BOOST_COLOR = { 2: { bg: '#1d4ed822', text: '#60a5fa', border: '#1d4ed855' }, 5: { bg: '#c2410c22', text: '#f97316', border: '#c2410c55' }, 10: { bg: '#9f121222', text: '#e10600', border: '#9f121255' } };
const GP_VALID_BOOSTS = [1, 2, 5, 10];

const UserAvatar = ({ user, size = 8 }) => {
  const [failed, setFailed] = useState(false);
  const initials = (user?.username || '?').slice(0, 2).toUpperCase();
  if (user?.avatar && !failed) {
    return (
      <img src={user.avatar} alt={user.username}
        className={`w-${size} h-${size} rounded-full object-cover bg-zinc-800 shrink-0`}
        onError={() => setFailed(true)} />
    );
  }
  return (
    <div className={`w-${size} h-${size} rounded-full bg-f1red/20 border border-f1red/40 flex items-center justify-center shrink-0`}>
      <span className="text-f1red font-black text-xs">{initials}</span>
    </div>
  );
};

const PronosticModal = ({ pronostic, race, liveResults, onClose }) => {
  if (!pronostic) return null;
  const { user, picks, sprintPicks, boosts, isDivine, score, sprintScore } = pronostic;
  const isRevealed = picks != null;
  const bst = boosts || {};
  const res = race?.results || {};
  const sprint = race?.sprintResults || {};
  const total = (score ?? 0) + (sprintScore ?? 0);

  const results = {
    winner:               res.winner               || liveResults?.standings?.[0]?.code || null,
    p2:                   res.p2                   || liveResults?.standings?.[1]?.code || null,
    p3:                   res.p3                   || liveResults?.standings?.[2]?.code || null,
    polePosition:         res.polePosition         || liveResults?.pole?.code          || null,
    fastestLap:           res.fastestLap           || liveResults?.fastestLap?.code    || null,
    fastestQualifyingLap: res.fastestQualifyingLap || null,
    q1Eliminated: res.q1Eliminated?.length  ? res.q1Eliminated  : (liveResults?.q1Eliminated  || []),
    q2Eliminated: res.q2Eliminated?.length  ? res.q2Eliminated  : (liveResults?.q2Eliminated  || []),
    safetyCar:        res.safetyCar        ?? liveResults?.safetyCar        ?? null,
    virtualSafetyCar: res.virtualSafetyCar ?? liveResults?.virtualSafetyCar ?? null,
    redFlag:          res.redFlag          ?? liveResults?.redFlag          ?? null,
  };
  const hasResults = !!(results.winner || results.polePosition || results.q1Eliminated?.length);

  const getBoost = (key) => {
    if (isDivine) return null;
    const b = Number(bst[key]);
    return GP_VALID_BOOSTS.includes(b) ? b : 1;
  };
  const calcPts = (base, correct, boost) => {
    if (isDivine) return correct ? base * 4 : 0;
    if (!boost || boost <= 1) return correct ? base : 0;
    return correct ? base * boost : -Math.round(base / boost);
  };

  const PickRow = ({ label, pick, result, base, boostKey, type = 'driver' }) => {
    const hasPick = pick !== null && pick !== undefined && pick !== '';
    if (!hasPick) return null;
    const boost = getBoost(boostKey);
    const noResult = (type === 'bool') ? (result === null || result === undefined) : !result;
    const correct  = noResult ? null : (type === 'bool') ? (pick === result) : (normGP(pick) === normGP(result));
    const pts      = (correct !== null && hasResults) ? calcPts(base, correct, boost ?? 1) : null;
    const pickLabel = type === 'bool' ? (pick ? 'Oui' : 'Non') : pick?.toUpperCase();
    const realLabel = noResult ? null : type === 'bool' ? (result ? 'Oui' : 'Non') : result?.toUpperCase();
    const c = (boost > 1) ? (GP_BOOST_COLOR[boost] || GP_BOOST_COLOR[10]) : null;
    return (
      <div className="py-2 border-b last:border-0" style={{ borderColor:'rgba(255,255,255,0.05)' }}>
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 text-xs shrink-0 w-24">{label}</span>
          <span className="text-white text-xs font-semibold flex-1">{pickLabel}</span>
          {hasResults && (
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-zinc-700 text-[10px]">→</span>
              <span className={`text-xs font-bold ${
                noResult ? 'text-zinc-600' : correct === true ? 'text-green-400' : 'text-red-400/80'
              }`}>{realLabel ?? '—'}</span>
              {correct === true  && <span className="text-green-500 text-[10px] font-black">✓</span>}
              {correct === false && <span className="text-red-500  text-[10px] font-black">✗</span>}
              <span className={`text-[11px] font-black min-w-[2.5rem] text-right ${
                pts > 0 ? 'text-green-400' : pts < 0 ? 'text-red-400' : 'text-zinc-600'
              }`}>
                {pts === null ? '' : pts > 0 ? `+${pts}` : pts === 0 ? '0' : `${pts}`}
                {pts !== null && <span className="text-zinc-600 font-normal text-[9px]"> pts</span>}
              </span>
            </div>
          )}
          {!hasResults && <span className="text-white text-xs font-semibold">{pickLabel}</span>}
        </div>
        {c && pts !== null && (
          <div className="flex items-center gap-1.5 mt-0.5" style={{ paddingLeft: '6.5rem' }}>
            <span className="text-[9px] px-1 py-0.5 rounded font-black" style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>×{boost}</span>
            {correct === true  && <span className="text-zinc-600 text-[9px]">{base}×{boost}=+{pts}</span>}
            {correct === false && <span className="text-zinc-600 text-[9px]">pén. −{Math.round(base/boost)}</span>}
          </div>
        )}
      </div>
    );
  };

  const EliminatedRow = ({ label, elPicks, resultCodes, base, boostKey }) => {
    if (!elPicks?.length) return null;
    const boost = getBoost(boostKey);
    const noResult = !resultCodes?.length || !hasResults;
    const matches = noResult ? [] : elPicks.filter(p => resultCodes.some(r => normGP(r) === normGP(p)));
    const pts = noResult ? null : matches.length * base * (boost ?? 1);
    const c = boost > 1 ? (GP_BOOST_COLOR[boost] || GP_BOOST_COLOR[10]) : null;
    return (
      <div className="py-2 border-b last:border-0" style={{ borderColor:'rgba(255,255,255,0.05)' }}>
        <div className="flex items-start gap-2 mb-1.5">
          <span className="text-zinc-500 text-xs shrink-0 w-24">{label}</span>
          <div className="flex-1" />
          {!noResult && (
            <span className={`text-[11px] font-black shrink-0 ${
              pts > 0 ? 'text-green-400' : 'text-zinc-600'
            }`}>{pts > 0 ? `+${pts}` : '0'}<span className="text-zinc-600 font-normal text-[9px]"> pts</span></span>
          )}
        </div>
        <div className="flex flex-wrap gap-1" style={{ paddingLeft: '6.5rem' }}>
          {elPicks.map(p => {
            const hit = !noResult && resultCodes.some(r => normGP(r) === normGP(p));
            return (
              <span key={p} className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold border ${
                noResult ? 'bg-zinc-800 text-zinc-400 border-zinc-700'
                : hit ? 'bg-green-900/40 text-green-400 border-green-800'
                       : 'bg-zinc-800/50 text-zinc-600 border-zinc-700/50 line-through'
              }`}>
                {!noResult && (hit ? '✓ ' : '✗ ')}{p.toUpperCase()}
              </span>
            );
          })}
        </div>
        {!noResult && (
          <div className="flex items-center gap-1.5 mt-1" style={{ paddingLeft: '6.5rem' }}>
            {c && <span className="text-[9px] px-1 py-0.5 rounded font-black" style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>×{boost}</span>}
            <span className="text-zinc-600 text-[9px]">{matches.length}/{elPicks.length} correct{matches.length > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
    );
  };

  const SectionLabel = ({ icon, title }) => (
    <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-1 flex items-center gap-1" style={{ color:'#e10600' }}>
      {icon} {title}
    </p>
  );

  return (
    <>
      <style>{`
        @keyframes pm-backdrop { from{opacity:0} to{opacity:1} }
        @keyframes pm-card {
          0%   { transform:scale(0.5) translateY(40px); opacity:0 }
          65%  { transform:scale(1.04) translateY(-4px); opacity:1 }
          82%  { transform:scale(0.98) translateY(2px) }
          100% { transform:scale(1) translateY(0); opacity:1 }
        }
        @keyframes pm-stripe { 0%{transform:scaleX(0)} 100%{transform:scaleX(1)} }
        @keyframes pm-in { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ animation:'pm-backdrop 0.25s ease forwards', background:'rgba(0,0,0,0.90)', backdropFilter:'blur(10px)' }}
        onClick={onClose}>
        <div className="relative w-full max-w-sm rounded-2xl overflow-hidden flex flex-col"
          style={{
            border:'2px solid #e10600',
            background:'linear-gradient(160deg,#0c0c10 0%,#e1060015 100%)',
            boxShadow:'0 0 50px #e1060045,0 0 100px #e1060018,inset 0 0 30px #e1060006',
            animation:'pm-card 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.05s both',
            maxHeight:'88vh',
          }}
          onClick={e => e.stopPropagation()}>

          <div style={{ height:4, background:'linear-gradient(90deg,#e10600 0%,transparent 80%)',
            animation:'pm-stripe 0.5s 0.15s ease-out both', transformOrigin:'left', flexShrink:0 }} />

          <div className="absolute top-0 right-0 flex opacity-20 pointer-events-none">
            {[...Array(6)].map((_,i) => (
              <div key={i} style={{ width:10, height:10, background: i%2===0?'#fff':'transparent' }} />
            ))}
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 shrink-0"
            style={{ animation:'pm-in 0.35s 0.3s ease both', opacity:0 }}>
            <div className="flex items-center gap-3">
              <UserAvatar user={user} size={10} />
              <div>
                <p className="text-white font-black text-base leading-tight">{user?.username}</p>
                {isDivine && <span className="text-[10px] text-yellow-400">⚡ Mode Divin</span>}
                {(score !== null && score !== undefined) && (
                  <p className="font-black text-sm" style={{ color:'#e10600' }}>
                    {total} pts{sprintScore > 0 ? ` (${sprintScore} sprint)` : ''}
                  </p>
                )}
              </div>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
              style={{ background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.1)' }}>
              <Icon name="x" size={14} className="text-zinc-400" />
            </button>
          </div>

          <div className="mx-5 h-px" style={{ background:'linear-gradient(90deg,#e10600 0%,transparent 80%)', opacity:0.3 }} />

          {/* Body */}
          <div className="px-5 py-4 overflow-y-auto flex flex-col gap-4"
            style={{ animation:'pm-in 0.35s 0.45s ease both', opacity:0 }}>
            {!isRevealed ? (
              <p className="text-zinc-500 text-sm text-center py-4">Les pronos sont verrouillés jusqu'aux résultats.</p>
            ) : (
              <>
                {/* Podium */}
                <div>
                  <SectionLabel icon="🏆" title="Podium" />
                  <PickRow label="Vainqueur" pick={picks?.winner} result={results.winner} base={25} boostKey="winner" />
                  <PickRow label="P2"        pick={picks?.p2}     result={results.p2}     base={18} boostKey="p2" />
                  <PickRow label="P3"        pick={picks?.p3}     result={results.p3}     base={15} boostKey="p3" />
                </div>

                {/* Qualifications */}
                <div>
                  <SectionLabel icon="⚡" title="Qualifications" />
                  <PickRow label="Pole"         pick={picks?.polePosition}         result={results.polePosition}         base={10} boostKey="polePosition" />
                  <PickRow label="Tour rapide Q" pick={picks?.fastestQualifyingLap} result={results.fastestQualifyingLap} base={8}  boostKey="fastestQualifyingLap" />
                  <EliminatedRow label="Éliminés Q1" elPicks={picks?.q1Eliminated} resultCodes={results.q1Eliminated} base={1} boostKey="q1Eliminated" />
                  <EliminatedRow label="Éliminés Q2" elPicks={picks?.q2Eliminated} resultCodes={results.q2Eliminated} base={2} boostKey="q2Eliminated" />
                </div>

                {/* Course */}
                <div>
                  <SectionLabel icon="🏁" title="Course" />
                  <PickRow label="Meilleur tour" pick={picks?.fastestLap} result={results.fastestLap} base={12} boostKey="fastestLap" />
                </div>

                {/* Événements */}
                {(picks?.safetyCar !== undefined || picks?.virtualSafetyCar !== undefined || picks?.redFlag !== undefined) && (
                  <div>
                    <SectionLabel icon="🚨" title="Événements" />
                    <PickRow label="Safety Car"    pick={picks?.safetyCar}        result={results.safetyCar}        base={4}  boostKey="safetyCar"        type="bool" />
                    <PickRow label="Virtual SC"    pick={picks?.virtualSafetyCar} result={results.virtualSafetyCar} base={2}  boostKey="virtualSafetyCar" type="bool" />
                    <PickRow label="Drapeau rouge" pick={picks?.redFlag}          result={results.redFlag}          base={10} boostKey="redFlag"          type="bool" />
                  </div>
                )}

                {/* Sprint */}
                {sprintPicks && (sprintPicks.winner || sprintPicks.p2 || sprintPicks.p3) && (
                  <div>
                    <SectionLabel icon="⚡" title="Sprint" />
                    <PickRow label="Vainqueur" pick={sprintPicks.winner} result={sprint.winner} base={12} boostKey="_" />
                    <PickRow label="P2"        pick={sprintPicks.p2}     result={sprint.p2}     base={10} boostKey="_" />
                    <PickRow label="P3"        pick={sprintPicks.p3}     result={sprint.p3}     base={8}  boostKey="_" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const StandingsTable = ({ standings, onDriverClick }) => {
  if (!standings?.length) return <p className="text-zinc-500 text-sm text-center py-8">Classement indisponible</p>;

  return (
    <div className="flex flex-col gap-2">
      {standings.slice(0, 3).map((d, i) => {
        const s = PODIUM_STYLE[i];
        return (
          <div key={i} onClick={() => onDriverClick?.(d)}
            className={`bg-gradient-to-r ${s.bg} border ${s.border} rounded-xl px-4 py-3 flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity`}>
            <span className="text-xl w-8 text-center">{s.medal}</span>
            <DriverAvatar driver={d} size={10} />
            <div className="flex-1 min-w-0">
              <p className="text-white font-black text-sm leading-tight">{d.fullName}</p>
              <p className="text-xs font-medium mt-0.5" style={{ color: teamColor(d.team) }}>{d.team || '—'}</p>
            </div>
            <Icon name="chevronDown" size={14} className="text-zinc-600 -rotate-90" />
          </div>
        );
      })}

      <div className="mt-3 border border-zinc-800 rounded-xl overflow-hidden">
        {standings.slice(3, 10).map((d, i) => (
          <div key={i} onClick={() => onDriverClick?.(d)}
            className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-800 last:border-0 hover:bg-zinc-800/50 transition-colors cursor-pointer">
            <span className="text-zinc-500 text-sm font-bold w-5 text-center">{i + 4}</span>
            <DriverAvatar driver={d} size={7} />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold leading-tight">{d.fullName}</p>
            </div>
            <p className="text-xs truncate max-w-[100px]" style={{ color: teamColor(d.team) }}>{d.team}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Rating helpers & component ──────────────────────────────── */
const ratingColor = (n) =>
  n <= 5 ? '#ef4444' : n <= 10 ? '#f97316' : n <= 15 ? '#eab308' : '#22c55e';

const RatingBadge = ({ avg, count, size = 'md' }) => {
  if (avg === null || avg === undefined) return null;
  const color = ratingColor(Math.round(avg));
  const big = size === 'lg';
  return (
    <div className="flex items-center gap-1.5">
      <span className={`font-black ${big ? 'text-2xl' : 'text-base'}`} style={{ color }}>{avg.toFixed(1)}</span>
      <span className={`text-zinc-500 ${big ? 'text-sm' : 'text-xs'}`}>/20</span>
      {count > 0 && <span className="text-zinc-600 text-xs">({count})</span>}
    </div>
  );
};

const RatingSection = ({ isPast, user, id, ratingsData, ratingInput, ratingLoading, onRate }) => {
  if (!isPast) return null;
  const { ratings, avgRating, ratingCount } = ratingsData;
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold text-white flex items-center gap-2">
          <span>⭐</span> Note ce GP
        </h2>
        {avgRating !== null && (
          <RatingBadge avg={avgRating} count={ratingCount} size="lg" />
        )}
      </div>

      {user ? (
        <>
          <p className="text-zinc-500 text-xs mb-3">
            {ratingInput ? `Ta note actuelle : ${ratingInput}/20 — clique pour modifier` : 'Clique sur une note de 1 à 20'}
          </p>
          <div className="grid grid-cols-10 gap-1">
            {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
              <button
                key={n}
                disabled={ratingLoading}
                onClick={() => onRate(n)}
                className={`h-9 rounded text-xs font-black transition-all ${
                  ratingInput === n ? 'scale-110 ring-2 ring-white/60' : 'hover:scale-105'
                } disabled:opacity-40`}
                style={{
                  background: ratingInput === n ? ratingColor(n) : `${ratingColor(n)}40`,
                  color: ratingInput === n ? '#fff' : ratingColor(n),
                }}
              >
                {n}
              </button>
            ))}
          </div>
        </>
      ) : (
        <p className="text-zinc-500 text-sm">Connecte-toi pour noter ce Grand Prix.</p>
      )}

      {ratings.length > 0 && (
        <div className="mt-5 pt-4 border-t border-zinc-800">
          <p className="text-xs uppercase tracking-widest text-zinc-600 mb-3">Notes des joueurs</p>
          <div className="flex flex-col gap-2">
            {[...ratings].sort((a, b) => b.rating - a.rating).map((r, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-f1red/20 border border-f1red/30 flex items-center justify-center shrink-0">
                  {r.user.avatar
                    ? <img src={r.user.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                    : <span className="text-f1red text-xs font-black">{r.user.username?.[0]?.toUpperCase()}</span>}
                </div>
                <span className="flex-1 text-zinc-300 text-sm">{r.user.username}</span>
                <span className="text-sm font-black" style={{ color: ratingColor(r.rating) }}>{r.rating}/20</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const GrandPrix = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [race, setRace] = useState(null);
  const [pronostics, setPronostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveResults, setLiveResults] = useState(null);
  const [resultsError, setResultsError] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [tab, setTab] = useState('schedule');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedPronostic, setSelectedPronostic] = useState(null);
  const [ratingsData, setRatingsData] = useState({ ratings: [], avgRating: null, ratingCount: 0 });
  const [ratingInput, setRatingInput] = useState(null);
  const [ratingLoading, setRatingLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/races/${id}`),
      user ? api.get(`/pronostics/${id}`) : Promise.resolve({ data: [] }),
    ]).then(([raceRes, pronosticsRes]) => {
      const r = raceRes.data;
      setRace(r);
      setPronostics(pronosticsRes.data);
      const raceDate = r.sessions?.race?.date
        ? new Date(`${r.sessions.race.date}T${r.sessions.race.time || '12:00:00Z'}`)
        : null;
      if (raceDate && raceDate < new Date()) {
        setTab('results');
        setResultsLoading(true);
        api.get(`/races/${id}/weekend-results`)
          .then(res => setLiveResults(res.data))
          .catch(err => {
            const msg = err.response?.data?.message;
            const status = err.response?.status;
            setResultsError(msg || (status === 429
              ? 'OpenF1 est temporairement indisponible (limite de requêtes). Réessaie dans quelques instants.'
              : 'Impossible de récupérer les résultats. Réessaie plus tard.'));
          })
          .finally(() => setResultsLoading(false));
        api.get(`/races/${id}/ratings`).then(res => {
          setRatingsData(res.data);
          if (user) {
            const mine = res.data.ratings.find(r => r.user._id === user.id);
            if (mine) setRatingInput(mine.rating);
          }
        }).catch(() => {});
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id, user]);

  const handleRate = async (note) => {
    if (!user || ratingLoading) return;
    setRatingLoading(true);
    try {
      const res = await api.post(`/races/${id}/rate`, { rating: note });
      setRatingInput(note);
      setRatingsData(prev => ({
        ...prev,
        avgRating: res.data.avgRating,
        ratingCount: res.data.ratingCount,
        ratings: prev.ratings.some(r => r.user._id === user.id)
          ? prev.ratings.map(r => r.user._id === user.id ? { ...r, rating: note } : r)
          : [...prev.ratings, { user: { _id: user.id, username: user.username, avatar: user.avatar }, rating: note }],
      }));
    } catch {}
    finally { setRatingLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-zinc-400">Chargement...</div>
  );
  if (!race) return (
    <div className="max-w-4xl mx-auto px-4 py-12 text-center text-zinc-400">Course introuvable.</div>
  );

  const now = new Date();
  const raceDate = race.sessions?.race?.date
    ? new Date(`${race.sessions.race.date}T${race.sessions.race.time || '12:00:00Z'}`) : null;
  const isPast = raceDate && raceDate < now;
  const deadline = race.deadline ? new Date(race.deadline) : null;
  const isOpen = !race.isLocked && (!deadline || now < deadline);
  const { sessions, circuit } = race;

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <Link to="/calendar" className="text-zinc-500 hover:text-zinc-300 text-sm flex items-center gap-1 mb-6">
        ← Retour au calendrier
      </Link>

      {/* Header */}
      <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
        <div>
          <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium">
            Round {race.round} · Saison {race.season} · {circuit?.country}
          </p>
          <h1 className="text-4xl font-black text-white mt-1 leading-tight">{race.raceName}</h1>
          <p className="text-zinc-400 text-sm mt-1">{circuit?.circuitName} · {circuit?.locality}</p>
          <div className="flex items-center gap-2 mt-3">
            {isPast ? (
              <span className="badge bg-zinc-700 text-zinc-300 flex items-center gap-1"><Icon name="flag" size={11} />Terminé</span>
            ) : race.isLocked ? (
              <span className="badge bg-zinc-700 text-zinc-300 flex items-center gap-1"><Icon name="lock" size={11} />Verrouillé</span>
            ) : (
              <span className="badge bg-f1red/10 text-f1red border border-f1red/30">Ouvert</span>
            )}
            {deadline && !isPast && (
              <span className="text-xs text-zinc-500">Deadline : {deadline.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
            )}
          </div>
        </div>
        <CircuitMap circuit={circuit} className="h-44" showLabel={false} />
      </div>

      {/* Tab toggle for past races */}
      {isPast && (
        <div className="flex gap-1 p-1 bg-zinc-900 border border-zinc-800 rounded-xl mb-6 w-fit">
          {['results', 'schedule'].map(t => (
            <button key={t} type="button" onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                tab === t ? 'bg-f1red text-white shadow' : 'text-zinc-400 hover:text-white'
              }`}>
              {t === 'results'
                ? <span className="flex items-center gap-1.5"><Icon name="trophy" size={13} />Résultats</span>
                : <span className="flex items-center gap-1.5"><Icon name="calendar" size={13} />Programme</span>}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 items-start">
        {/* Left: schedule or results */}
        <div className="card">
          {tab === 'schedule' ? (
            <>
              <h2 className="font-bold text-white mb-4 flex items-center gap-2"><Icon name="calendar" size={16} className="text-zinc-400" /> Programme</h2>
              <SessionRow label="Essais Libres 1" session={sessions?.fp1} />
              <SessionRow label="Essais Libres 2" session={sessions?.fp2} />
              <SessionRow label="Essais Libres 3" session={sessions?.fp3} />
              <SessionRow label="Sprint" session={sessions?.sprint} />
              <SessionRow label="Qualifications" session={sessions?.qualifying} />
              <SessionRow label="Course" session={sessions?.race} />
            </>
          ) : resultsLoading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <div className="w-8 h-8 border-2 border-f1red border-t-transparent rounded-full animate-spin" />
              <p className="text-zinc-500 text-sm">Chargement des résultats OpenF1...</p>
            </div>
          ) : liveResults ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-white flex items-center gap-2"><Icon name="trophy" size={16} className="text-yellow-400" /> Résultats de la course</h2>
                {liveResults.fetchedAt && (
                  <span className="text-[10px] text-zinc-600">via OpenF1</span>
                )}
              </div>

              {liveResults.pole && (
                <div className="flex items-center gap-3 bg-zinc-800/50 rounded-lg px-3 py-2 mb-3 cursor-pointer hover:bg-zinc-800 transition-colors" onClick={() => setSelectedDriver(liveResults.pole)}>
                  <Icon name="lightning" size={16} className="text-yellow-400 shrink-0" />
                  <DriverAvatar driver={liveResults.pole} size={8} />
                  <div className="min-w-0">
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Pole Position</p>
                    <p className="text-white text-sm font-bold truncate">{liveResults.pole.fullName}</p>
                  </div>
                </div>
              )}
              {liveResults.fastestLap && (
                <div className="flex items-center gap-3 bg-purple-900/20 border border-purple-800/30 rounded-lg px-3 py-2 mb-4 cursor-pointer hover:bg-purple-900/30 transition-colors" onClick={() => setSelectedDriver(liveResults.fastestLap)}>
                  <Icon name="clock" size={16} className="text-purple-400 shrink-0" />
                  <DriverAvatar driver={liveResults.fastestLap} size={8} />
                  <div className="min-w-0">
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Meilleur Tour</p>
                    <p className="text-white text-sm font-bold truncate">{liveResults.fastestLap.fullName}</p>
                  </div>
                </div>
              )}

              <StandingsTable standings={liveResults.standings} onDriverClick={setSelectedDriver} />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-center px-4">
              <p className="text-zinc-500 text-sm">Résultats non disponibles</p>
              {resultsError && <p className="text-red-400 text-xs bg-red-900/20 border border-red-800 rounded px-3 py-1.5">{resultsError}</p>}
            </div>
          )}
        </div>

        {/* Right: pronostic + details */}
        <div className="flex flex-col gap-6">
          <div className="card">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2"><Icon name="target" size={16} className="text-f1red" /> Ton pronostic</h2>
            {!user ? (
              <div className="text-center py-4">
                <p className="text-zinc-400 text-sm mb-4">Connecte-toi pour faire ton pronostic.</p>
                <Link to="/login" className="btn-primary inline-block">Se connecter</Link>
              </div>
            ) : isOpen ? (
              <div className="text-center py-4">
                <p className="text-zinc-400 text-sm mb-4">Les pronos sont ouverts !</p>
                <Link to={`/grand-prix/${id}/pronostic`} className="btn-primary inline-block">
                  Faire mon pronostic →
                </Link>
              </div>
            ) : (
              <div className="text-center py-4">
                {isPast ? (
                  <>
                    <p className="text-zinc-500 text-sm mb-3">Course terminée.</p>
                    <Link to={`/grand-prix/${id}/pronostic`} className="btn-secondary inline-block text-sm">
                      Voir mes picks →
                    </Link>
                  </>
                ) : (
                  <p className="text-zinc-400 text-sm">Les pronos sont verrouillés.</p>
                )}
              </div>
            )}
          </div>

          {/* Details below pronostic */}
          {isPast && (() => {
            const res = race.results || {};
            const q1Eliminated = liveResults?.q1Eliminated?.length ? liveResults.q1Eliminated : (res.q1Eliminated || []);
            const q2Eliminated = liveResults?.q2Eliminated?.length ? liveResults.q2Eliminated : (res.q2Eliminated || []);
            const safetyCar        = liveResults?.safetyCar        ?? res.safetyCar;
            const virtualSafetyCar = liveResults?.virtualSafetyCar ?? res.virtualSafetyCar;
            const redFlag          = liveResults?.redFlag          ?? res.redFlag;
            const hasQ1 = q1Eliminated.length > 0;
            const hasQ2 = q2Eliminated.length > 0;
            const hasFastestLap = liveResults?.fastestLap || res.fastestLap;
            const hasEvents = safetyCar != null || virtualSafetyCar != null || redFlag != null;
            const hasSprint = race.sprintResults?.winner;
            if (!hasQ1 && !hasQ2 && !hasFastestLap && !hasEvents && !hasSprint) return null;
            const fastestLapDisplay = liveResults?.fastestLap?.fullName || res.fastestLap || null;

            const EventRow = ({ icon, label, value }) => {
              if (value == null) return null;
              return (
                <div className="flex items-center justify-between py-1.5 border-b border-zinc-800 last:border-0">
                  <span className="text-zinc-400 text-sm flex items-center gap-1.5"><Icon name={icon} size={13} /> {label}</span>
                  <span className={`text-sm font-bold ${value ? 'text-green-400' : 'text-zinc-500'}`}>
                    {value ? 'Oui' : 'Non'}
                  </span>
                </div>
              );
            };

            return (
              <div className="card flex flex-col gap-4">
                {hasFastestLap && (
                  <div className="flex items-center gap-3 bg-purple-900/20 border border-purple-800/30 rounded-lg px-3 py-2">
                    <Icon name="clock" size={15} className="text-purple-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Meilleur Tour Course</p>
                      <p className="text-white text-sm font-bold">{fastestLapDisplay}</p>
                    </div>
                  </div>
                )}
                {hasQ1 && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-widest font-medium mb-2">Éliminés Q1</p>
                    <div className="flex flex-wrap gap-1.5">
                      {q1Eliminated.map(code => (
                        <span key={code} className="text-xs font-bold bg-zinc-800 text-zinc-300 border border-zinc-700 px-2 py-0.5 rounded-md">{code}</span>
                      ))}
                    </div>
                  </div>
                )}
                {hasQ2 && (
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-widest font-medium mb-2">Éliminés Q2</p>
                    <div className="flex flex-wrap gap-1.5">
                      {q2Eliminated.map(code => (
                        <span key={code} className="text-xs font-bold bg-zinc-800 text-zinc-300 border border-zinc-700 px-2 py-0.5 rounded-md">{code}</span>
                      ))}
                    </div>
                  </div>
                )}
                {hasEvents && (
                  <div>
                    <EventRow icon="warning" label="Safety Car" value={safetyCar} />
                    <EventRow icon="clock" label="Virtual Safety Car" value={virtualSafetyCar} />
                    <EventRow icon="flag" label="Drapeau rouge" value={redFlag} />
                  </div>
                )}
                {hasSprint && (
                  <div className={hasQ1 || hasQ2 || hasEvents || hasFastestLap ? 'pt-3 border-t border-zinc-800' : ''}>
                    <p className="text-xs text-orange-400 uppercase tracking-widest font-medium mb-2 flex items-center gap-1"><Icon name="lightning" size={11} /> Sprint</p>
                    {[['P1', race.sprintResults.winner], ['P2', race.sprintResults.p2], ['P3', race.sprintResults.p3]].map(([pos, code]) => code ? (
                      <div key={pos} className="flex items-center justify-between py-1.5 border-b border-zinc-800 last:border-0">
                        <span className="text-zinc-400 text-sm">{pos}</span>
                        <span className="text-white text-sm font-bold">{code}</span>
                      </div>
                    ) : null)}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Per-race leaderboard */}
      {user && pronostics.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-white flex items-center gap-2">
              <Icon name="chart" size={16} className="text-f1red" />
              Classement du GP ({pronostics.length} participants)
            </h2>
            <Link to="/leaderboard" className="text-xs text-zinc-500 hover:text-f1red transition-colors">Général →</Link>
          </div>
          {pronostics.some(p => p.score !== null && p.score !== undefined) ? (
            <div className="flex flex-col gap-0">
              {[...pronostics]
                .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
                .map((p, i) => (
                  <div key={p._id}
                    onClick={() => setSelectedPronostic(p)}
                    className="flex items-center gap-3 py-2.5 border-b border-zinc-800 last:border-0 cursor-pointer hover:bg-zinc-800/40 rounded-lg px-2 -mx-2 transition-colors">
                    <span className={`text-sm font-black w-5 text-center shrink-0 ${
                      i === 0 ? 'text-yellow-400' : i === 1 ? 'text-zinc-300' : i === 2 ? 'text-orange-400' : 'text-zinc-600'
                    }`}>{i + 1}</span>
                    <UserAvatar user={p.user} size={7} />
                    <span className="flex-1 font-medium text-white text-sm truncate">{p.user?.username}</span>
                    {p.isDivine && <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-400 border border-yellow-500/30 shrink-0">Dieu a décidé</span>}
                    <span className="font-black text-f1red shrink-0">{p.score} pts</span>
                  </div>
                ))}
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {pronostics.map(p => (
                <div key={p._id}
                  onClick={() => setSelectedPronostic(p)}
                  className="py-2.5 flex items-center gap-3 cursor-pointer hover:bg-zinc-800/40 rounded-lg px-2 -mx-2 transition-colors">
                  <UserAvatar user={p.user} size={7} />
                  <span className="flex-1 font-medium text-white text-sm truncate">{p.user?.username}</span>
                  {p.isDivine && <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-400 border border-yellow-500/30 shrink-0">Dieu a décidé</span>}
                  <span className="text-zinc-500 text-xs flex items-center gap-1"><Icon name="check" size={11} />Soumis</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <RatingSection
        isPast={isPast}
        user={user}
        id={id}
        ratingsData={ratingsData}
        ratingInput={ratingInput}
        ratingLoading={ratingLoading}
        onRate={handleRate}
      />

      <FullResultsSection raceId={id} isPast={isPast} />
      <RaceStatusSection raceId={id} raceData={race} user={user} />
      <CommentsSection raceId={id} user={user} />

      {selectedDriver && (
        <DriverModal driver={selectedDriver} season={SEASON} onClose={() => setSelectedDriver(null)} />
      )}
      {selectedPronostic && (
        <PronosticModal pronostic={selectedPronostic} race={race} liveResults={liveResults} onClose={() => setSelectedPronostic(null)} />
      )}
    </div>
  );
};

const REACTIONS = ['🔥', '👀', '💀'];

const CommentsSection = ({ raceId, user }) => {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.get(`/races/${raceId}/comments`).then(r => setComments(r.data)).catch(() => {});
  }, [raceId]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true); setError('');
    try {
      const r = await api.post(`/races/${raceId}/comments`, { text });
      setComments(prev => [r.data, ...prev]); setText('');
    } catch (err) { setError(err.response?.data?.message || 'Erreur'); }
    finally { setPosting(false); }
  };

  const remove = async (commentId) => {
    try { await api.delete(`/races/${raceId}/comments/${commentId}`); setComments(prev => prev.filter(c => c._id !== commentId)); } catch {}
  };

  const react = async (commentId, emoji) => {
    try {
      const r = await api.post(`/races/${raceId}/comments/${commentId}/react`, { emoji });
      setComments(prev => prev.map(c => c._id === commentId ? { ...c, reactions: r.data.reactions } : c));
    } catch {}
  };

  const myId = user?.id || user?._id;
  const fmtDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="mt-8 rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
        <Icon name="flag" size={14} className="text-f1red" />
        <h3 className="text-white font-black text-sm uppercase tracking-wider">Commentaires</h3>
        {comments.length > 0 && <span className="ml-auto text-xs text-zinc-500 font-medium">{comments.length}</span>}
      </div>

      {user && (
        <form onSubmit={submit} className="px-5 py-4 border-b border-zinc-800/60 flex gap-3 items-start">
          <UserAvatar user={user} size={8} />
          <div className="flex-1 flex flex-col gap-2">
            <textarea value={text} onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(e); } }}
              placeholder="Ton analyse, ton avis, ton trash talk..." maxLength={500} rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-500 resize-none focus:outline-none focus:border-zinc-500 transition-colors" />
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">{text.length}/500 · Entrée pour envoyer</span>
              <button type="submit" disabled={posting || !text.trim()}
                className="px-4 py-1.5 rounded-lg text-xs font-black bg-f1red text-white disabled:opacity-40 hover:bg-red-600 transition-colors">
                {posting ? '...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="divide-y divide-zinc-800/60">
        {comments.length === 0 ? (
          <p className="text-center text-zinc-600 text-sm py-8">Pas encore de commentaires — sois le premier !</p>
        ) : comments.map(c => {
          const isMe = String(c.user?._id) === String(myId);
          const rxMap = c.reactions || {};
          return (
            <div key={c._id} className="px-5 py-3.5 flex gap-3 group">
              <UserAvatar user={c.user} size={8} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-white text-xs font-black">{c.user?.username}</span>
                  <span className="text-zinc-600 text-[10px]">{fmtDate(c.createdAt)}</span>
                  {isMe && (
                    <button onClick={() => remove(c._id)}
                      className="ml-auto text-[10px] text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
                      Supprimer
                    </button>
                  )}
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap break-words mb-2">{c.text}</p>
                <div className="flex gap-1.5 flex-wrap">
                  {REACTIONS.map(emoji => {
                    const users = rxMap[emoji] || [];
                    const hasReacted = users.map(String).includes(String(myId));
                    return (
                      <button key={emoji} onClick={() => user && react(c._id, emoji)}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-all ${hasReacted ? 'bg-zinc-700 border-zinc-500 text-white' : 'bg-transparent border-zinc-700 text-zinc-500 hover:border-zinc-500 hover:text-zinc-300'}`}>
                        <span>{emoji}</span>
                        {users.length > 0 && <span className="font-bold">{users.length}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FullResultsSection = ({ raceId, isPast }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (data || loading) return;
    setLoading(true);
    api.get(`/races/${raceId}/full-results`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [raceId, data, loading]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!isPast) return null;

  const TEAM_PTS_COLORS = { 1:'text-yellow-400', 2:'text-zinc-300', 3:'text-orange-400' };
  const ptColor = (pos) => TEAM_PTS_COLORS[pos] || (pos <= 10 ? 'text-zinc-400' : 'text-zinc-600');

  return (
    <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full px-5 py-4 flex items-center gap-2 hover:bg-zinc-800/40 transition-colors">
        <Icon name="trophy" size={14} className="text-yellow-400" />
        <h3 className="text-white font-black text-sm uppercase tracking-wider flex-1 text-left">Classement complet</h3>
        <span className="text-zinc-500 text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="border-t border-zinc-800">
          {loading && <p className="text-center text-zinc-500 text-sm py-6">Chargement...</p>}
          {!loading && !data && <p className="text-center text-zinc-600 text-sm py-6">Données non disponibles pour ce GP.</p>}
          {data && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left px-4 py-2 text-[10px] text-zinc-500 font-bold uppercase w-8">Pos</th>
                    <th className="text-left px-4 py-2 text-[10px] text-zinc-500 font-bold uppercase">Pilote</th>
                    <th className="text-left px-4 py-2 text-[10px] text-zinc-500 font-bold uppercase hidden sm:table-cell">Écurie</th>
                    <th className="text-right px-4 py-2 text-[10px] text-zinc-500 font-bold uppercase">Temps / Statut</th>
                    <th className="text-right px-4 py-2 text-[10px] text-zinc-500 font-bold uppercase w-10">Pts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/60">
                  {data.results.map(r => (
                    <tr key={r.position} className="hover:bg-zinc-800/30 transition-colors">
                      <td className={`px-4 py-2.5 font-black text-center ${ptColor(r.position)}`}>{r.position}</td>
                      <td className="px-4 py-2.5">
                        <span className="text-white font-bold">{r.code || r.fullName}</span>
                        {r.fastestLap?.rank === 1 && <span className="ml-1 text-[10px] text-purple-400 font-black">⚡FL</span>}
                      </td>
                      <td className="px-4 py-2.5 text-zinc-400 hidden sm:table-cell truncate max-w-[120px]">{r.team}</td>
                      <td className="px-4 py-2.5 text-right text-zinc-400 text-xs font-mono">
                        {r.position === 1
                          ? (r.time || '—')
                          : r.time ? `+${r.time}` : r.status !== 'Finished' ? r.status : '—'}
                      </td>
                      <td className={`px-4 py-2.5 text-right font-black ${r.points > 0 ? 'text-f1red' : 'text-zinc-600'}`}>{r.points || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RaceStatusSection = ({ raceId, raceData, user }) => {
  const raceDate = raceData?.sessions?.race?.date
    ? new Date(`${raceData.sessions.race.date}T${raceData.sessions.race.time || '14:00:00Z'}`) : null;
  const isPast = raceDate && raceDate < new Date();
  const isLocked = raceData?.isLocked;

  const [statuses, setStatuses] = useState([]);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [myStatus, setMyStatus] = useState(null);

  const myId = user?.id || user?._id;

  useEffect(() => {
    api.get(`/races/${raceId}/statuses`).then(r => {
      setStatuses(r.data.statuses || []);
      const mine = r.data.statuses?.find(s => String(s.user?._id) === String(myId));
      if (mine) { setMyStatus(mine); setText(mine.text); }
    }).catch(() => {});
  }, [raceId, myId]);

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const r = await api.put(`/races/${raceId}/statuses`, { text });
      setMyStatus(r.data);
      setStatuses(prev => prev.find(s => String(s.user?._id) === String(myId))
        ? prev.map(s => String(s.user?._id) === String(myId) ? r.data : s)
        : [r.data, ...prev]);
    } catch {}
    setSaving(false);
  };

  const rate = async (statusId, value) => {
    try {
      const r = await api.post(`/races/${raceId}/statuses/${statusId}/rate`, { value });
      setStatuses(prev => prev.map(s => s._id === statusId ? { ...s, _avgRating: r.data.avg, _ratingCount: r.data.count } : s));
    } catch {}
  };

  const stars = (s) => {
    const myRating = s.ratings?.find(r => String(r.user) === String(myId))?.value;
    return (
      <div className="flex gap-0.5 mt-1">
        {[1,2,3,4,5].map(v => (
          <button key={v} onClick={() => isPast && String(s.user?._id) !== String(myId) && rate(s._id, v)}
            className={`text-sm transition-colors ${v <= (myRating || 0) ? 'text-yellow-400' : 'text-zinc-600 hover:text-yellow-400/50'} ${String(s.user?._id) === String(myId) || !isPast ? 'pointer-events-none' : ''}`}>★</button>
        ))}
        {(s._avgRating || s.ratings?.length > 0) && (
          <span className="text-[10px] text-zinc-500 ml-1">{s._avgRating?.toFixed(1) || (s.ratings?.reduce((a,r)=>a+r.value,0)/s.ratings?.length).toFixed(1)}/5 ({s._ratingCount || s.ratings?.length})</span>
        )}
      </div>
    );
  };

  if (!isLocked && !isPast && !user) return null;

  return (
    <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
        <span className="text-base">🔮</span>
        <h3 className="text-white font-black text-sm uppercase tracking-wider">Prédictions avant-course</h3>
        {!isPast && <span className="ml-auto text-[10px] text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded-full">Révélées après la course</span>}
      </div>

      {user && !isPast && (
        <div className="px-5 py-4 border-b border-zinc-800/60 flex gap-3 items-start">
          <UserAvatar user={user} size={8} />
          <div className="flex-1 flex flex-col gap-2">
            <textarea value={text} onChange={e => setText(e.target.value)}
              placeholder="Je prévois deux crashs pendant la course..." maxLength={200} rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-500 resize-none focus:outline-none focus:border-zinc-500 transition-colors" />
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600">{text.length}/200</span>
              <button onClick={save} disabled={saving || !text.trim()}
                className="px-4 py-1.5 rounded-lg text-xs font-black bg-zinc-700 text-white disabled:opacity-40 hover:bg-zinc-600 transition-colors">
                {myStatus ? 'Modifier' : 'Publier'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="divide-y divide-zinc-800/60">
        {!isPast && statuses.length === 0 && (
          <p className="text-center text-zinc-600 text-sm py-6">Aucune prédiction pour l'instant.</p>
        )}
        {!isPast && statuses.length > 0 && (
          <p className="text-center text-zinc-500 text-xs py-4 italic">Les prédictions des autres seront révélées après la course.</p>
        )}
        {isPast && statuses.length === 0 && (
          <p className="text-center text-zinc-600 text-sm py-6">Personne n'avait soumis de prédiction pour ce GP.</p>
        )}
        {isPast && statuses.map(s => (
          <div key={s._id} className="px-5 py-3.5 flex gap-3">
            <UserAvatar user={s.user} size={8} />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-white text-xs font-black">{s.user?.username}</span>
                <span className="text-zinc-600 text-[10px]">{new Date(s.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed">"{s.text}"</p>
              {String(s.user?._id) !== String(myId) && stars(s)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GrandPrix;
